import { type System, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type { AmmoComponent, TimeScaleResource } from '@game/scenes/characterPrototype/types';
import { clamp } from '@game/scenes/characterPrototype/math';

export type AmmoSystemOptions = {
	ammoClipSize: number;
};

export function createAmmoSystem(ammos: ComponentStore<AmmoComponent>, options: AmmoSystemOptions): System {
	return ({ delta, world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const timescale = world.getResource<TimeScaleResource>('timescale') ?? ({ value: 1 } as TimeScaleResource);
		const dt = delta * timescale.value;
		if (dt <= 0) return;

		ammos.forEach((ammo) => {
			ammo.capacity = Math.max(1, Math.round(ammo.capacity));
			ammo.remaining = clamp(Math.round(ammo.remaining), 0, ammo.capacity);
			ammo.reloadDuration = Math.max(0.05, ammo.reloadDuration);
			if (!Number.isFinite(ammo.reloadRemaining) || ammo.reloadRemaining < 0) ammo.reloadRemaining = 0;

			// Keep reload duration in sync with tuning (hot-tunable).
			if (ammo.isEnemy) {
				ammo.reloadDuration = Math.max(0.05, tuning.enemyReloadDuration);
				ammo.capacity = options.ammoClipSize;
			} else {
				ammo.reloadDuration = Math.max(0.05, tuning.playerReloadDuration);
				ammo.capacity = options.ammoClipSize;
			}
			ammo.remaining = clamp(ammo.remaining, 0, ammo.capacity);

			if (ammo.reloadRemaining > 0) {
				ammo.reloadRemaining = Math.max(0, ammo.reloadRemaining - dt);
				if (ammo.reloadRemaining <= 0) {
					ammo.reloadRemaining = 0;
					ammo.remaining = ammo.capacity;
				}
			} else if (ammo.remaining <= 0) {
				// Safety: start reload if something consumed ammo to 0 without scheduling reload.
				ammo.reloadRemaining = ammo.reloadDuration;
			}
		});
	};
}


